import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SUPPORT_MAX_ATTACHMENTS,
  SUPPORT_MESSAGE_MAX,
  SUPPORT_POLL_SECONDS,
  TICKET_STATUS_LABELS,
  type ReasonPicker,
  type SupportLanguage,
  type SupportMessage,
  type SupportRequesterRealm,
  type TicketCreateInput,
  type TicketView,
} from '@capsico/contracts';
import { errorMessage, errorStatus, errorRef } from '../api/client';
import {
  askForAgent,
  createTicket,
  fetchReasons,
  fetchTicket,
  fetchTickets,
  postMessage,
  rateTicket,
  requestCall,
  setTicketLanguage,
  uploadSupportPhoto,
} from '../api/support';
import { useFormDraft } from './useFormDraft';
import { showToast } from './useToast';
import {
  LOCAL_PREFIX,
  keepNewer,
  mergeSupportMessage,
  newOpKey,
  photoProblem,
  replaceLocalMessage,
} from './supportMerge';
import { supportTicketKey, supportTicketsKey, useSupportLive } from './useSupportLive';

/*
 * Help + chat state (W16). Screens stay presentation-only: every fetch, cache rule, optimistic
 * change, upload and draft lives here. Reads are react-query (stale-while-revalidate); actions show
 * at once and roll back if the server refuses.
 */

const reasonsKey = (orderId: string | null) => ['support-reasons', orderId ?? 'none'] as const;

/* ---------------- my tickets ---------------- */

export function useMyTickets(enabled = true) {
  const query = useQuery({
    queryKey: supportTicketsKey,
    queryFn: fetchTickets,
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
  const rows = query.data?.rows ?? [];
  return {
    loading: query.isLoading,
    failed: query.isError,
    retry: () => void query.refetch(),
    rows,
    openCount: rows.filter((row) => row.status !== 'resolved' && row.status !== 'closed').length,
  };
}

/* ---------------- reason picker ---------------- */

export function useReasonPicker(orderId: string | null, enabled = true) {
  const query = useQuery({
    queryKey: reasonsKey(orderId),
    queryFn: () => fetchReasons(orderId ?? undefined),
    enabled,
    staleTime: 60_000,
    retry: (count, error) => errorStatus(error) !== 404 && count < 2,
  });
  return {
    loading: query.isLoading,
    failed: query.isError && errorStatus(query.error) !== 404,
    notFound: errorStatus(query.error) === 404,
    retry: () => void query.refetch(),
    picker: query.data ?? null,
  };
}

/* ---------------- photos (shared by the new-ticket form and the chat) ---------------- */

export interface PhotoSlot {
  key: string;
  /** Local preview (object URL) — shown at once, before the upload ends. */
  preview: string;
  attachmentId: string | null;
  /** 0–100 while uploading, null when done or failed. */
  progress: number | null;
  failed: boolean;
}

export interface PhotoMessages {
  badType: string;
  tooBig: string;
  tooMany: string;
  uploadFailed: string;
}

function usePhotoSlots(messages: PhotoMessages) {
  const [slots, setSlots] = useState<PhotoSlot[]>([]);
  const live = useRef(new Set<string>());

  // Free the previews when the screen goes away.
  useEffect(() => {
    const urls = live.current;
    return () => {
      for (const url of urls) URL.revokeObjectURL(url);
      urls.clear();
    };
  }, []);

  const patch = (key: string, change: Partial<PhotoSlot>) =>
    setSlots((current) =>
      current.map((slot) => (slot.key === key ? { ...slot, ...change } : slot)),
    );

  const add = (file: File) => {
    const problem = photoProblem(file);
    if (problem) {
      showToast(problem === 'type' ? messages.badType : messages.tooBig, { tone: 'negative' });
      return;
    }
    if (slots.length >= SUPPORT_MAX_ATTACHMENTS) {
      showToast(messages.tooMany, { tone: 'negative' });
      return;
    }
    const key = newOpKey('photo');
    const preview = URL.createObjectURL(file);
    live.current.add(preview);
    setSlots((current) => [
      ...current,
      { key, preview, attachmentId: null, progress: 0, failed: false },
    ]);
    uploadSupportPhoto(file, (percent) => patch(key, { progress: percent }))
      .then((done) => {
        if (!done.uploaded) throw new Error('not uploaded');
        patch(key, { attachmentId: done.attachmentId, progress: null });
      })
      .catch(() => {
        patch(key, { progress: null, failed: true });
        showToast(messages.uploadFailed, { tone: 'negative' });
      });
  };

  const remove = (key: string) =>
    setSlots((current) => {
      const slot = current.find((entry) => entry.key === key);
      if (slot) {
        URL.revokeObjectURL(slot.preview);
        live.current.delete(slot.preview);
      }
      return current.filter((entry) => entry.key !== key);
    });

  const reset = () => {
    for (const slot of slots) {
      URL.revokeObjectURL(slot.preview);
      live.current.delete(slot.preview);
    }
    setSlots([]);
  };

  const uploaded = slots.filter((slot) => slot.attachmentId !== null);
  return {
    slots,
    add,
    remove,
    reset,
    uploading: slots.some((slot) => slot.progress !== null),
    attachmentIds: uploaded.map((slot) => slot.attachmentId as string),
    previews: uploaded.map((slot) => ({ id: slot.attachmentId as string, url: slot.preview })),
    canAdd: slots.length < SUPPORT_MAX_ATTACHMENTS,
  };
}

/* ---------------- new ticket ---------------- */

interface NewTicketDraft {
  reasonCode: string;
  items: Record<string, number>;
  message: string;
}

const EMPTY_DRAFT: NewTicketDraft = { reasonCode: '', items: {}, message: '' };
const isEmptyDraft = (value: NewTicketDraft) =>
  value.reasonCode === '' && value.message.trim() === '' && Object.keys(value.items).length === 0;

/**
 * Opening a ticket: pick a reason, the order's items and photos when the reason needs them, an
 * optional note. The typed form is draft-saved per order (Keep / Discard) and cleared once sent;
 * photos are never drafted. A retry of the same submit reuses its op key, so the server never
 * opens two tickets for one tap.
 */
export function useNewTicket(
  orderId: string | null,
  picker: ReasonPicker | null,
  options: { language?: SupportLanguage; onCreated: (ticket: TicketView) => void },
  messages: PhotoMessages & { failed: string },
) {
  const client = useQueryClient();
  const [form, setForm] = useState<NewTicketDraft>(EMPTY_DRAFT);
  const photos = usePhotoSlots(messages);
  const attempt = useRef<{ key: string; body: string } | null>(null);

  const draft = useFormDraft(`support-ticket:${orderId ?? 'new'}`, form, {
    enabled: picker !== null,
    isEmpty: isEmptyDraft,
    base: EMPTY_DRAFT,
  });

  const reason = picker?.reasons.find((entry) => entry.code === form.reasonCode) ?? null;
  const itemsById = useMemo(
    () => new Map((picker?.items ?? []).map((item) => [item.orderItemId, item])),
    [picker],
  );
  const picked = Object.entries(form.items)
    .filter(([id, quantity]) => quantity > 0 && itemsById.has(id))
    .map(([orderItemId, quantity]) => ({ orderItemId, quantity }));

  const create = useMutation({
    mutationFn: (input: TicketCreateInput) => {
      const body = JSON.stringify(input);
      if (!attempt.current || attempt.current.body !== body) {
        attempt.current = { key: newOpKey('ticket'), body };
      }
      return createTicket(input, attempt.current.key);
    },
    onSuccess: (view) => {
      attempt.current = null;
      client.setQueryData(supportTicketKey(view.id), view);
      void client.invalidateQueries({ queryKey: supportTicketsKey });
      void client.invalidateQueries({ queryKey: reasonsKey(orderId) });
      draft.clear();
      setForm(EMPTY_DRAFT);
      photos.reset();
      options.onCreated(view);
    },
    onError: (error) =>
      showToast(errorMessage(error, messages.failed), { tone: 'negative', ref: errorRef(error) }),
  });

  const missingItems = reason?.needsItems === true && picked.length === 0;
  const missingPhoto = reason?.needsPhoto === true && photos.attachmentIds.length === 0;
  const canSubmit =
    reason !== null && !missingItems && !missingPhoto && !photos.uploading && !create.isPending;

  const submit = () => {
    if (!reason || !canSubmit) return;
    const note = form.message.trim();
    create.mutate({
      ...(orderId ? { orderId } : {}),
      reasonCode: reason.code,
      items: reason.needsItems ? picked : [],
      attachmentIds: photos.attachmentIds,
      ...(note ? { message: note } : {}),
      ...(options.language ? { language: options.language } : {}),
    });
  };

  /** One-tap reasons (rider trip screen): no items, photos or note needed. */
  const quick = (code: string) => {
    if (create.isPending) return;
    const note = form.message.trim();
    create.mutate({
      ...(orderId ? { orderId } : {}),
      reasonCode: code,
      items: [],
      attachmentIds: photos.attachmentIds,
      ...(note ? { message: note } : {}),
      ...(options.language ? { language: options.language } : {}),
    });
  };

  return {
    reason,
    reasonCode: form.reasonCode,
    setReason: (code: string) => setForm((current) => ({ ...current, reasonCode: code })),
    quantities: form.items,
    setQuantity: (orderItemId: string, quantity: number) => {
      const max = itemsById.get(orderItemId)?.quantity ?? 0;
      const next = Math.max(0, Math.min(max, quantity));
      setForm((current) => {
        const items = { ...current.items };
        if (next === 0) delete items[orderItemId];
        else items[orderItemId] = next;
        return { ...current, items };
      });
    },
    message: form.message,
    setMessage: (value: string) =>
      setForm((current) => ({ ...current, message: value.slice(0, SUPPORT_MESSAGE_MAX) })),
    maxLength: SUPPORT_MESSAGE_MAX,
    photos,
    missingItems,
    missingPhoto,
    canSubmit,
    submitting: create.isPending,
    quickCode: create.isPending ? (create.variables?.reasonCode ?? null) : null,
    submit,
    quick,
    draftRestored: draft.restored !== null,
    keepDraft: () => {
      const value = draft.keep();
      if (value) setForm({ ...EMPTY_DRAFT, ...value });
    },
    discardDraft: draft.discard,
  };
}

/* ---------------- ticket chat ---------------- */

export interface ChatMessages extends PhotoMessages {
  sendFailed: string;
  actionFailed: string;
  agentAsked: string;
  callAsked: string;
  rated: string;
}

interface CsatDraft {
  score: number;
  comment: string;
}
const EMPTY_CSAT: CsatDraft = { score: 0, comment: '' };
const isEmptyText = (value: string) => value.trim() === '';
const isEmptyCsat = (value: CsatDraft) => value.score === 0 && value.comment.trim() === '';

/** One optimistic ticket action: show the change at once, keep the server copy, roll back on error. */
function useTicketAction<V>(
  ticketId: string,
  failed: string,
  run: (variables: V) => Promise<TicketView>,
  change: (view: TicketView, variables: V) => Partial<TicketView>,
  done?: string,
) {
  const client = useQueryClient();
  const key = supportTicketKey(ticketId);
  return useMutation({
    mutationFn: run,
    onMutate: async (variables: V) => {
      await client.cancelQueries({ queryKey: key, exact: true });
      const previous = client.getQueryData<TicketView>(key);
      if (previous) {
        client.setQueryData<TicketView>(key, { ...previous, ...change(previous, variables) });
      }
      return { previous };
    },
    onSuccess: (saved) => {
      client.setQueryData<TicketView | undefined>(key, (current) => keepNewer(current, saved));
      void client.invalidateQueries({ queryKey: supportTicketsKey });
      if (done) showToast(done, { tone: 'positive' });
    },
    onError: (error, _variables, context) => {
      if (context?.previous) client.setQueryData(key, context.previous);
      showToast(errorMessage(error, failed), { tone: 'negative', ref: errorRef(error) });
    },
  });
}

/**
 * One ticket's chat: the thread (bot + agent + you), live through the ticket room with a 15 s poll
 * while the socket is down; sending is optimistic (the bubble shows at once and is taken back if
 * the server refuses); "Talk to an agent", language switch, request a call and the 1–5 rating
 * after resolve, each optimistic with rollback. The typed reply and the rating comment are
 * draft-saved per ticket and cleared once sent.
 */
export function useTicketChat(
  ticketId: string,
  realm: SupportRequesterRealm,
  messages: ChatMessages,
) {
  const client = useQueryClient();
  const key = supportTicketKey(ticketId);
  const live = useSupportLive(ticketId, ticketId !== '');

  const query = useQuery({
    queryKey: key,
    queryFn: async () =>
      keepNewer(client.getQueryData<TicketView>(key), await fetchTicket(ticketId)),
    enabled: ticketId !== '',
    staleTime: 10_000,
    retry: (count, error) => errorStatus(error) !== 404 && count < 2,
    refetchInterval: (current) =>
      live.polling && current.state.data?.status !== 'closed' ? SUPPORT_POLL_SECONDS * 1000 : false,
  });
  const view = query.data ?? null;

  /* composer */
  const [text, setText] = useState('');
  const photos = usePhotoSlots(messages);
  const draft = useFormDraft(`support-chat:${ticketId}`, text, {
    enabled: view !== null,
    isEmpty: isEmptyText,
    base: '',
  });

  const send = useMutation({
    mutationFn: (input: { body: string; attachmentIds: string[]; tempId: string }) =>
      postMessage(ticketId, { body: input.body, attachmentIds: input.attachmentIds }),
    onMutate: async (input) => {
      await client.cancelQueries({ queryKey: key, exact: true });
      const optimistic: SupportMessage = {
        id: input.tempId,
        ticketId,
        sender: realm,
        senderName: null,
        body: input.body,
        attachments: photos.previews
          .filter((photo) => input.attachmentIds.includes(photo.id))
          .map((photo) => ({ id: photo.id, contentType: 'image/jpeg', url: photo.url })),
        internal: false,
        at: new Date().toISOString(),
      };
      client.setQueryData<TicketView | undefined>(key, (current) =>
        mergeSupportMessage(current, optimistic),
      );
    },
    onSuccess: (saved, input) => {
      client.setQueryData<TicketView | undefined>(key, (current) =>
        replaceLocalMessage(current, input.tempId, saved),
      );
      void client.invalidateQueries({ queryKey: supportTicketsKey });
    },
    onError: (error, input) => {
      client.setQueryData<TicketView | undefined>(key, (current) =>
        replaceLocalMessage(current, input.tempId, null),
      );
      if (input.body) setText(input.body);
      showToast(errorMessage(error, messages.sendFailed), {
        tone: 'negative',
        ref: errorRef(error),
      });
    },
  });

  const agent = useTicketAction(
    ticketId,
    messages.actionFailed,
    () => askForAgent(ticketId),
    () => ({ status: 'queued', statusLabel: TICKET_STATUS_LABELS.queued }),
    messages.agentAsked,
  );
  const language = useTicketAction(
    ticketId,
    messages.actionFailed,
    (next: SupportLanguage) => setTicketLanguage(ticketId, next),
    (_view, next) => ({ language: next }),
  );
  const [callNote, setCallNote] = useState('');
  const [callOpen, setCallOpen] = useState(false);
  const callDraft = useFormDraft(`support-call:${ticketId}`, callNote, {
    enabled: callOpen,
    isEmpty: isEmptyText,
    base: '',
  });
  const call = useTicketAction(
    ticketId,
    messages.actionFailed,
    (note: string) => requestCall(ticketId, note ? { note } : {}),
    () => ({ callRequestedAt: new Date().toISOString() }),
    messages.callAsked,
  );

  /* rating */
  const [csat, setCsat] = useState<CsatDraft>(EMPTY_CSAT);
  const csatDraft = useFormDraft(`support-csat:${ticketId}`, csat, {
    enabled: view?.canRate === true,
    isEmpty: isEmptyCsat,
    base: EMPTY_CSAT,
  });
  const rate = useTicketAction(
    ticketId,
    messages.actionFailed,
    (input: CsatDraft) =>
      rateTicket(ticketId, {
        score: input.score,
        ...(input.comment.trim() ? { comment: input.comment.trim() } : {}),
      }),
    (_view, input) => ({ csatScore: input.score, canRate: false }),
    messages.rated,
  );

  const trimmed = text.trim();
  const sendNow = useCallback(() => {
    if ((!trimmed && photos.attachmentIds.length === 0) || photos.uploading) return;
    send.mutate({
      body: trimmed,
      attachmentIds: photos.attachmentIds,
      tempId: `${LOCAL_PREFIX}${newOpKey('msg')}`,
    });
    setText('');
    photos.reset();
    draft.clear();
  }, [trimmed, photos, send, draft]);

  const status = view?.status ?? null;
  return {
    loading: query.isLoading,
    notFound: errorStatus(query.error) === 404,
    failed: query.isError && errorStatus(query.error) !== 404,
    retry: () => void query.refetch(),
    view,
    /** Show the "Reconnecting… updating every 15 s" strip. */
    reconnecting: live.polling,
    open: status !== null && status !== 'resolved' && status !== 'closed',
    canAskAgent: status === 'bot',
    askAgent: () => agent.mutate(undefined),
    askingAgent: agent.isPending,
    language: view?.language ?? null,
    setLanguage: (next: SupportLanguage) => {
      if (view && view.language !== next) language.mutate(next);
    },
    canCall: status !== null && status !== 'closed' && !view?.callRequestedAt,
    callOpen,
    openCall: () => setCallOpen(true),
    closeCall: () => setCallOpen(false),
    callNote,
    setCallNote: (value: string) => setCallNote(value.slice(0, 200)),
    requestCall: () => {
      call.mutate(callNote.trim(), {
        onSuccess: () => {
          callDraft.clear();
          setCallNote('');
          setCallOpen(false);
        },
      });
    },
    calling: call.isPending,
    callDraftRestored: callDraft.restored !== null,
    keepCallDraft: () => {
      const value = callDraft.keep();
      if (typeof value === 'string') setCallNote(value);
    },
    discardCallDraft: callDraft.discard,
    text,
    setText: (value: string) => setText(value.slice(0, SUPPORT_MESSAGE_MAX)),
    maxLength: SUPPORT_MESSAGE_MAX,
    photos,
    canSend:
      view?.canReply === true &&
      (trimmed.length > 0 || photos.attachmentIds.length > 0) &&
      !photos.uploading &&
      !send.isPending,
    sending: send.isPending,
    send: sendNow,
    draftRestored: draft.restored !== null,
    keepDraft: () => {
      const value = draft.keep();
      if (typeof value === 'string') setText(value);
    },
    discardDraft: draft.discard,
    csat,
    setScore: (score: number) => setCsat((current) => ({ ...current, score })),
    setComment: (comment: string) =>
      setCsat((current) => ({ ...current, comment: comment.slice(0, 300) })),
    canSubmitRating: csat.score >= 1 && csat.score <= 5 && !rate.isPending,
    rating: rate.isPending,
    submitRating: () => {
      if (csat.score < 1) return;
      rate.mutate(csat, {
        onSuccess: () => {
          csatDraft.clear();
          setCsat(EMPTY_CSAT);
        },
      });
    },
    csatDraftRestored: csatDraft.restored !== null,
    keepCsatDraft: () => {
      const value = csatDraft.keep();
      if (value) setCsat(value);
    },
    discardCsatDraft: csatDraft.discard,
  };
}

/* ---------------- view models ---------------- */

export interface BubbleView {
  id: string;
  side: 'me' | 'them' | 'system';
  who: string | null;
  body: string;
  photos: { id: string; url: string }[];
  at: string;
  pending: boolean;
}

/** Chat bubbles for the thread: yours on the right, the assistant and agents on the left. */
export function supportBubbles(
  messages: readonly SupportMessage[],
  realm: SupportRequesterRealm,
  names: { bot: string; agent: string; team: string },
): BubbleView[] {
  return messages
    .filter((message) => !message.internal)
    .map((message) => ({
      id: message.id,
      side: message.sender === realm ? 'me' : message.sender === 'system' ? 'system' : 'them',
      who:
        message.sender === realm || message.sender === 'system'
          ? null
          : message.sender === 'bot'
            ? names.bot
            : message.sender === 'agent'
              ? (message.senderName ?? names.agent)
              : names.team,
      body: message.body,
      photos: message.attachments
        .filter((photo): photo is typeof photo & { url: string } => photo.url !== null)
        .map((photo) => ({ id: photo.id, url: photo.url })),
      at: message.at,
      pending: message.id.startsWith(LOCAL_PREFIX),
    }));
}
